/**
 * Befunde und Kopf-Texte des Kapitels „Kanäle & Werbeflächen" (Persona-Umbau D, 28.10.2026). Je Karte EIN Satz, beim
 * Rendern aus den Daten gerechnet, beschreibend — keine Ursache. Zu dünne oder fehlende Daten ergeben `{ fehlt: Grund }`.
 * Muster wie `hebel/befund.ts`. Rein (keine React-Abhängigkeit), damit jest die Sätze prüfen kann.
 */
import { euroJeTausend, formatDelta, kompakt } from '../../../components/auswertungen/kennzahlTeile';
import type { Veraenderung } from '../../../helpers/auswertungenData';
import { formatNumber } from '../../../helpers/numberUtils';
import type { DmHaelften, WerbeflaechenKern } from './personaDaten';

export type Befund = { text: string } | { fehlt: string };

/** „07.05." */
const tm = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
/** Exklusives Ende → letzter Tag (YYYY-MM-DD). */
const vorTag = (bisExklusiv: string): string => {
  const d = new Date(`${bisExklusiv.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};


/** Prozentpunkte mit Vorzeichen, Kurzform „−5,1 Pp" (Tabellenzellen, Kachel-Hauptwerte) oder Langform „−5,1 Prozentpunkte" (Befunde, Kachel-Unterzeilen; Audit C2). */
export function ppText(pp: number | null | undefined, form: 'kurz' | 'lang' = 'kurz'): string {
  if (pp === null || pp === undefined) return '—';
  const r = Number(pp.toFixed(1));
  return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${formatNumber(Math.abs(r), 1)} ${form === 'lang' ? 'Prozentpunkte' : 'Pp'}`;
}

/** Richtung einer Veränderung (gerundet auf ganze Prozent wie die Anzeige): ▲, ▼ oder nichts bei ±0. */
export function richtung(delta: number | null | undefined): '▲' | '▼' | null {
  if (delta === null || delta === undefined) return null;
  const r = Math.round(delta);
  return r > 0 ? '▲' : r < 0 ? '▼' : null;
}

/** Versandfenster der beiden Hälften: „15.07.–14.10." und „15.04.–14.07.". */
export function haelftenFenster(h: Pick<DmHaelften, 'letzte_von' | 'davor_von' | 'versand_bis'>): { letzte: string; davor: string } {
  return {
    letzte: `${tm(h.letzte_von)}–${tm(vorTag(h.versand_bis))}`,
    davor: `${tm(h.davor_von)}–${tm(vorTag(h.letzte_von))}`,
  };
}

/**
 * Befund der Rangkarte (Plan D2): „Erwarteter Wert je 1.000 Kontakte: Direkt-Mailings 44 €, Heat-Ups 18 € — Kontakte der
 * Direkt-Mailings in den letzten 3 Monaten 427 Tsd. gegen 760 Tsd. davor (Versände …)". Ohne die Hälften nur der erste Teil.
 * Bewusst KEIN Faktor „das N-Fache": er liest sich als Umschichtungs-Empfehlung (Festlegung P3 vom 20.10.), und beide
 * Werte zählen über verschiedene Zuordnungen. `minAnfos`: darunter kein Befund (das Verhältnis hinge am Zufall).
 */
export function rangBefund(kern: WerbeflaechenKern | undefined, h: DmHaelften | null, minAnfos: number): Befund {
  if (!kern || kern.faktor === null || kern.direkt_mailing.wert_je_1000 === null || kern.heat_up.wert_je_1000 === null) {
    return { fehlt: 'Kein Befund: Direkt-Mailings oder Heat-Ups haben im Zeitraum keine Kontaktzahl.' };
  }
  const duenn = [
    kern.direkt_mailing.anfos < minAnfos ? 'Direkt-Mailings' : null,
    kern.heat_up.anfos < minAnfos ? 'Heat-Ups' : null,
  ].filter(Boolean);
  if (duenn.length > 0) return { fehlt: `Kein Befund: ${duenn.join(' und ')} unter ${minAnfos} Anfos — das Verhältnis hinge am Zufall.` };
  // „zusammen (…)": die 44 € stehen in keiner Zeile der Rangfolge (Runde 2, Marketing: sechs Direkt-Mailing-Werte).
  const kopf = `Erwarteter Wert je 1.000 Kontakte: Direkt-Mailings zusammen (Finanztrends, Schwestermarken, Partnerlisten) ${euroJeTausend(kern.direkt_mailing.wert_je_1000)}, Heat-Ups ${euroJeTausend(kern.heat_up.wert_je_1000)}`;
  if (!h) return { text: `${kopf}.` };
  const fe = haelftenFenster(h);
  return {
    text: `${kopf} — Kontakte der Direkt-Mailings in den letzten 3 Monaten ${kompakt(h.letzte.kontakte)} gegen ${kompakt(h.davor.kontakte)} davor (Versände ${fe.letzte} gegen ${fe.davor}).`,
  };
}

/**
 * Befund der Kanal-Karte zu den Direkt-Mailings (Plan D3), Menge und Quote getrennt: „Direkt-Mailings −48 % Erwarteter
 * Wert gegen 29.04.–27.10.2025; Versände 15.07.–14.10. gegen 15.04.–14.07.: Kontakte −44 %, € je 1.000 −19 % (38 € gegen 47 €)".
 * Die Zerlegung läuft über die zwei Versandhälften, weil es Kontakte je Versand erst seit 23.12.2025 gibt (kein Vorjahr).
 */
export function dmKanalBefund(v: Veraenderung | null | undefined, basisText: string, h: DmHaelften | null, minAnfos: number): Befund {
  if (!v || v.neu || v.delta === null) return { fehlt: 'Kein Befund: die Veränderung der Direkt-Mailings ist nicht rechenbar.' };
  const kopf = `Direkt-Mailings ${formatDelta(v.delta)} Erwarteter Wert gegen ${basisText}${v.grau_grund ? ' (wenig Anfos)' : ''}`;
  if (!h || h.delta_kontakte === null || h.delta_wert_je_1000 === null) return { text: `${kopf}.` };
  const fe = haelftenFenster(h);
  const duenn = h.letzte.anfos < minAnfos || h.davor.anfos < minAnfos ? ', wenig Anfos' : '';
  return {
    text: `${kopf}. Menge und Quote getrennt, Versände ${fe.letzte} gegen ${fe.davor}: Kontakte ${formatDelta(h.delta_kontakte)}, € je 1.000 ${formatDelta(h.delta_wert_je_1000)} (${euroJeTausend(h.letzte.wert_je_1000)} gegen ${euroJeTausend(h.davor.wert_je_1000)}${duenn}).`,
  };
}

/**
 * Kanäle, deren Kennung im Bestell-Link sich geändert hat (Plan D3: Warnhinweis nur an diesen Zeilen). Quelle: der bis
 * 28.10.2026 pauschale Hinweis der Kanal-Karte; eine Gruppe trägt die Marke, wenn einer ihrer Kanäle sie trägt.
 */
export const KENNUNG_GEAENDERT: Record<string, string> = {
  itm: 'Die ITM-Kennungen gibt es nicht mehr.',
  doi_ft: 'Bestätigungsseiten laufen heute öfter über die Schwestermarken als über Finanztrends.',
  doi_brands: 'Bestätigungsseiten laufen heute öfter über die Schwestermarken als über Finanztrends.',
  redlink: 'Der Newsletter-Redlink zum Produkt hat eine eigene Kennung, die es früher nicht gab.',
  other_token: 'Ältere Aktionen tragen seltene Kennungen.',
};

/** Grund der Marke „Kennung geändert" für einen Kanal bzw. eine Gruppe (Kanäle der Gruppe), sonst `null`. */
export function kennungGeaendert(kanaele: string[]): string | null {
  const gruende = [...new Set(kanaele.map((k) => KENNUNG_GEAENDERT[k]).filter((g): g is string => !!g))];
  return gruende.length > 0 ? gruende.join(' ') : null;
}
